import React from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import {
  FaTachometerAlt,
  FaCogs,
  FaGasPump,
  FaCar,
  FaCalendarAlt,
  FaIdCard,
} from 'react-icons/fa';

const VehicleDetailsCard = ({ vehicleData }) => {
  // Extract data from API response
  const vehicleInfo = vehicleData?.vehicle_info || {};
  const aiScore = vehicleData?.ai_score || 0;
  const valuation = vehicleData?.valuation || 0;
  const vin = vehicleData?.vin || '';

  // console.log('gas values', vehicleInfo.fuel_economy)
  return (
    <Box
      borderWidth="1px"
      borderColor="gray.300"
      borderRadius="md"
      overflow="hidden"
      bg="white"
      boxShadow="sm"
    >
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        bg="#212e89"
        color="white"
        px={4}
        py={2}
      >
        <Flex align="center" gap={4}>
          <Heading size={{ base: "md", md: "lg" }} fontWeight="semibold">
            {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
          </Heading>
          <Icon as={FaCar} boxSize={8} />
        </Flex>
        {/* <Badge colorScheme="green" fontSize="sm" px={2} py={1}>
          AI Score: {aiScore}/100
        </Badge> */}
      </Flex>

      <Box p={6}>
        {/* Expandable Sections */}
        <Accordion allowToggle defaultIndex={[]}>
          {/* Mileage Section */}
          {vehicleInfo.mileage && (
            <AccordionItem border="none" borderTop="1px solid" borderColor="gray.200">
              <AccordionButton
                px={0}
                py={3}
                _hover={{ bg: 'gray.50' }}
                _expanded={{ color: '#212e89', bg: 'gray.50' }}
              >
                <Flex align="center" gap={2} flex="1" textAlign="left">
                  <Icon as={FaTachometerAlt} color="blue.500" />
                  <Text fontWeight="semibold" fontSize="md">
                    Mileage
                  </Text>
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4} px={0}>
                <Box pl={8}>
                  <Text fontSize="md" fontWeight="medium" color="gray.800" mb={1}>
                    {vehicleInfo.mileage?.toLocaleString()} miles
                  </Text>
                </Box>
              </AccordionPanel>
            </AccordionItem>
          )}
          {/* Engine Section */}
          {vehicleInfo.engine && (
            <AccordionItem border="none" borderTop="1px solid" borderColor="gray.200">
              <AccordionButton
                px={0}
                py={3}
                _hover={{ bg: 'gray.50' }}
                _expanded={{ color: '#212e89', bg: 'gray.50' }}
              >
                <Flex align="center" gap={2} flex="1" textAlign="left">
                  <Icon as={FaIdCard} color="blue.500" />
                  <Text fontWeight="semibold" fontSize="md">
                    Engine
                  </Text>
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4} px={0}>
                <Box pl={8}>
                  <Text fontSize="md" fontWeight="medium" color="gray.800" mb={1}>
                    {vehicleInfo.engine}
                  </Text>
                </Box>
              </AccordionPanel>
            </AccordionItem>
          )}

          {/* Fuel Economy Section */}
          {vehicleInfo.fuel_economy && (
            <AccordionItem border="none" borderTop="1px solid" borderColor="gray.200">
              <AccordionButton
                px={0}
                py={3}
                _hover={{ bg: 'gray.50' }}
                _expanded={{ color: '#212e89', bg: 'gray.50' }}
              >
                <Flex align="center" gap={2} flex="1" textAlign="left">
                  <Icon as={FaGasPump} color="blue.500" />
                  <Text fontWeight="semibold" fontSize="md">
                    Fuel Economy
                  </Text>
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4} px={0}>
                <Box pl={8}>
                  <Text fontSize="md" fontWeight="medium" color="gray.800" mb={1}>
                    {vehicleInfo.fuel_economy}
                  </Text>
                </Box>
              </AccordionPanel>
            </AccordionItem>
          )}

          {/* Transmission & Drivetrain Section */}
          {(vehicleInfo.transmission || vehicleInfo.drivetrain) && (
            <AccordionItem border="none" borderTop="1px solid" borderColor="gray.200">
              <AccordionButton
                px={0}
                py={3}
                _hover={{ bg: 'gray.50' }}
                _expanded={{ color: '#212e89', bg: 'gray.50' }}
              >
                <Flex align="center" gap={2} flex="1" textAlign="left">
                  <Icon as={FaCogs} color="blue.500" />
                  <Text fontWeight="semibold" fontSize="md">
                    Transmission & Drivetrain
                  </Text>
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4} px={0}>
                <Box pl={8}>
                  {vehicleInfo.transmission && (
                    <Flex align="center" gap={2} mb={3}>
                      <Text fontSize="sm" color="gray.600" minW="120px">
                        Transmission:
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {vehicleInfo.transmission}
                      </Text>
                    </Flex>
                  )}
                  {vehicleInfo.drivetrain && (
                    <Flex align="center" gap={2}>
                      {/* <Icon as={FaCar} color="orange.400" /> */}
                      <Text fontSize="sm" color="gray.600" minW="120px">
                        Drivetrain:
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {vehicleInfo.drivetrain}
                      </Text>
                    </Flex>
                  )}
                </Box>
              </AccordionPanel>
            </AccordionItem>
          )}

          {/* Vehicle Information Section */}
          {(vehicleInfo.year || vehicleInfo.make || vehicleInfo.model) && (
            <AccordionItem border="none" borderTop="1px solid" borderColor="gray.200">
              <AccordionButton
                px={0}
                py={3}
                _hover={{ bg: 'gray.50' }}
                _expanded={{ color: '#212e89', bg: 'gray.50' }}
              >
                <Flex align="center" gap={2} flex="1" textAlign="left">
                  <Icon as={FaCalendarAlt} color="blue.500" />
                  <Text fontWeight="semibold" fontSize="md">
                    Vehicle Information
                  </Text>
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4} px={0}>
                <Box pl={8}>
                  {vehicleInfo.year && (
                    <Flex align="center" gap={2} mb={2}>
                      <Text fontSize="sm" color="gray.600" minW="120px">
                        Year:
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {vehicleInfo.year}
                      </Text>
                    </Flex>
                  )}
                  {vehicleInfo.make && (
                    <Flex align="center" gap={2} mb={2}>
                      <Text fontSize="sm" color="gray.600" minW="120px">
                        Make:
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {vehicleInfo.make}
                      </Text>
                    </Flex>
                  )}
                  {vehicleInfo.model && (
                    <Flex align="center" gap={2}>
                      <Text fontSize="sm" color="gray.600" minW="120px">
                        Model:
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {vehicleInfo.model}
                      </Text>
                    </Flex>
                  )}
                </Box>
              </AccordionPanel>
            </AccordionItem>
          )}
        </Accordion>
      </Box>
    </Box>
  );
};

export default VehicleDetailsCard;
